import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { MdChevronLeft, MdClose, MdOutlineRefresh } from 'react-icons/md';
import StatsCard from '@/components/StatsCard';
import StatusBadge from '@/components/StatusBadge';
import ViewButton from '@/components/ViewButton';
import CaseViewModal, { type CaseRow } from '@/components/CaseViewModal';
import { DataTable } from '@podus/ui';
import type { Column } from '@podus/ui';
import { api } from '@/lib/api';
import { exportCSV } from '@/lib/csv';
import type { StaffMember, Case, CaseStats } from '@/types';

/* ─── Helpers ─── */

function mapToRow(c: Case, index: number): CaseRow {
    const applicant = c.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant');
    return {
        id: c.id,
        sn: index + 1,
        date: new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        complainantName: applicant?.name ?? c.caseNumber,
        caseCategory: c.caseCategory,
        disputeCategory: c.disputeCategory,
        evidence: c.evidence?.length ?? 'Nil',
        amountPaid: `₦${Number(c.amountPaid).toLocaleString()}`,
        status: c.status,
    };
}

const TABS = ['All', 'New', 'Assigned', 'In Progress', 'Resolved'];

/* ─── Component ─── */

export default function InternalUserView() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [staff, setStaff] = useState<StaffMember | null>(null);
    const [cases, setCases] = useState<CaseRow[]>([]);
    const [stats, setStats] = useState<CaseStats | null>(null);
    const [activeTab, setActiveTab] = useState('All');
    const [selectedCase, setSelectedCase] = useState<CaseRow | null>(null);
    const [isBlocking, setIsBlocking] = useState(false);
    const [isResending, setIsResending] = useState(false);

    useEffect(() => {
        if (!id) return;
        api.get<StaffMember>(`/api/staff/${id}`)
            .then((res) => setStaff(res.response.data ?? null))
            .catch(() => {});

        api.get<CaseStats>(`/api/cases/stats`)
            .then((res) => setStats(res.response.data ?? null))
            .catch(() => {});
    }, [id]);

    useEffect(() => {
        if (!id) return;
        const statusParam = activeTab !== 'All' ? `&status=${encodeURIComponent(activeTab)}` : '';
        api.get<Case[]>(`/api/cases?assignedCaseManagerId=${id}${statusParam}&limit=100`)
            .then((res) => setCases((res.response.data ?? []).map(mapToRow)))
            .catch(() => setCases([]));
    }, [id, activeTab]);

    const handleBlock = async () => {
        if (!id || !staff) return;
        setIsBlocking(true);
        try {
            await api.patch(`/api/staff/${id}/block`, {});
            setStaff((prev) => (prev ? { ...prev, status: prev.status === 'blocked' ? 'active' : 'blocked' } : prev));
        } catch {
            // silently ignore
        } finally {
            setIsBlocking(false);
        }
    };

    const handleResend = async () => {
        if (!id) return;
        setIsResending(true);
        try {
            await api.post(`/api/staff/${id}/resend-invitation`, {});
        } catch {
            // silently ignore
        } finally {
            setIsResending(false);
        }
    };

    const initials =
        staff?.name
            ?.split(' ')
            .map((w) => w[0])
            .join('')
            .slice(0, 2)
            .toUpperCase() ?? '??';

    const statsCards = [
        { label: 'All Cases Managed', value: String(stats?.total ?? 0) },
        { label: 'New', value: String(stats?.New ?? 0) },
        { label: 'Assigned', value: String(stats?.Assigned ?? 0) },
        { label: 'In Progress', value: String(stats?.['In Progress'] ?? 0) },
        { label: 'Resolved', value: String(stats?.Resolved ?? 0) },
    ];

    const COLUMNS: Column<CaseRow>[] = [
        { key: 'sn', label: 'S/N' },
        { key: 'date', label: 'Date' },
        { key: 'complainantName', label: 'Complainant Name' },
        { key: 'caseCategory', label: 'Case Category' },
        { key: 'disputeCategory', label: 'Dispute Category' },
        { key: 'amountPaid', label: 'Amount Paid' },
        {
            key: 'status',
            label: 'Case Status',
            render: (row) => <StatusBadge status={row.status} />,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => <ViewButton onClick={() => setSelectedCase(row)} />,
        },
    ];

    return (
        <div className="space-y-6  py-6">
            <div className="flex flex-col gap-4 px-4 lg:flex-row lg:items-start lg:gap-6 lg:px-8">
                <div className="flex items-start gap-3">
                    <button
                        type="button"
                        title="Back to Internal Users"
                        onClick={() => navigate('/dashboard/users')}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#eee] text-[#555] transition-colors hover:bg-[#f5f5f5]"
                    >
                        <MdChevronLeft size={20} />
                    </button>

                    <div className="rounded-xl border border-[#eee] px-5 py-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e8e8e8] text-sm font-bold text-secondary">
                                {initials}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <p className="text-[15px] font-bold text-secondary">{staff?.name ?? '…'}</p>
                                    {staff?.status === 'active' && (
                                        <span className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[12px] font-medium text-green-600">
                                            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                            Active
                                        </span>
                                    )}
                                    {staff?.status === 'blocked' && (
                                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-[12px] font-medium text-red-600">
                                            Blocked
                                        </span>
                                    )}
                                </div>
                                <p className="text-[13px] text-[#888]">{staff?.email ?? ''}</p>
                            </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between border-t border-[#eee] pt-2">
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleBlock}
                                    disabled={isBlocking}
                                    className="flex items-center gap-1 text-[13px] font-medium text-red-500 transition-colors hover:text-red-700 disabled:opacity-50"
                                >
                                    <MdClose size={14} />
                                    {staff?.status === 'blocked' ? 'Unblock' : 'Block'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={isResending}
                                    className="flex items-center gap-1 text-[13px] font-medium text-[#555] transition-colors hover:text-secondary disabled:opacity-50"
                                >
                                    <MdOutlineRefresh size={14} />
                                    Resend Invitation
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid flex-1 grid-cols-3 gap-3 sm:grid-cols-5">
                    {statsCards.map((s) => (
                        <StatsCard key={s.label} stat={s} />
                    ))}
                </div>
            </div>

            <DataTable
                title="Cases"
                columns={COLUMNS}
                data={cases}
                getRowKey={(row) => row.id}
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onExportCSV={() =>
                    exportCSV(`staff-${staff?.name ?? id}-cases`, COLUMNS, cases)
                }
            />

            <CaseViewModal open={!!selectedCase} onClose={() => setSelectedCase(null)} caseData={selectedCase} />
        </div>
    );
}
